/**
 * The story runner's step function and manifest (spec 009). The runner plays
 * the story through spec 020's `StoryRuntime`; this module decides what the
 * editor shows after each scene (the next scene, "End of story", or "This
 * choice isn't connected to a scene yet") with the same graph rules the
 * runtime follows, and builds the manifest the runtime plays from the
 * editor's in-memory scenes.
 */

import type {
  PlaybackSettings,
  Scene,
  SheetCut,
  StoryApiResponse,
  StoryGraph,
  StoryPackageManifest,
} from '../../../Shared/src/@types';
import { toPlayableScene, type PlayableSceneInput } from '../../../Shared/src/runtime/playableScene';
import { MANIFEST_FORMAT_VERSION, RUNTIME_VERSION } from '../../../Shared/src/runtime/version';
import { knownSheets } from '../stores/mediaSheetStore';

/** How a scene ended: on its own (`auto`), or by an option (clicked or timed out). */
export type PreviewEvent =
  | { type: 'auto' }
  | { type: 'choice'; choiceId: string };

export type PreviewStep =
  /** Play the scene at this node next. */
  | { kind: 'scene'; nodeId: string; storySceneId: number }
  /** The story ends here: no outgoing edge, an end node, or a scene that is not available. */
  | { kind: 'end' }
  /** The chosen option has no arrow. */
  | { kind: 'unconnected'; choiceId: string };

/**
 * What follows the scene at `nodeId` after `event` (the rules of spec 020's
 * `StoryRuntime`): a choice follows the edge whose `condition` is its id,
 * `auto` follows the unconditional edge. `scenesByStorySceneId` holds the
 * scenes the preview has; a target whose scene is missing ends the story.
 */
export function nextPreviewStep(
  graph: StoryGraph,
  scenesByStorySceneId: Record<number, unknown> | Map<number, unknown>,
  nodeId: string,
  event: PreviewEvent,
): PreviewStep {
  const outgoing = graph.edges.filter((e) => e.from === nodeId);
  const edge = event.type === 'choice'
    ? outgoing.find((e) => e.condition === event.choiceId)
    : outgoing.find((e) => !e.condition);
  if (!edge) {
    return event.type === 'choice' ? { kind: 'unconnected', choiceId: event.choiceId } : { kind: 'end' };
  }
  const target = graph.nodes.find((n) => n.id === edge.to);
  if (!target || target.type === 'end') return { kind: 'end' };
  const has = scenesByStorySceneId instanceof Map
    ? scenesByStorySceneId.has(target.storySceneId)
    : Object.prototype.hasOwnProperty.call(scenesByStorySceneId, target.storySceneId);
  if (!has) return { kind: 'end' };
  return { kind: 'scene', nodeId: target.id, storySceneId: target.storySceneId };
}

/**
 * A story manifest for `StoryRuntime` from the story and the preview's scenes
 * (keyed by `StoryScene.id`). Scenes go through `toPlayableScene` exactly as
 * story publish writes them, with the spritesheet cuts of their media
 * (`sheets`, spec 033; default: the cuts the editor has loaded).
 */
export function buildPreviewManifest(
  story: Pick<StoryApiResponse, 'id' | 'name' | 'graph' | 'storyScenes'>,
  scenesByStorySceneId: Record<number, PlayableSceneInput>,
  playbackSettings?: PlaybackSettings,
  sheets: Record<number, SheetCut> = knownSheets(),
): StoryPackageManifest {
  const sceneMap: Record<number, Scene> = {};
  const order: string[] = [];
  const scenes: Scene[] = [];
  for (const ss of story.storyScenes) {
    const input = scenesByStorySceneId[ss.id];
    if (!input) continue;
    const playable = toPlayableScene({ ...input, name: input.name ?? ss.sceneName ?? '' }, { sheets }) as unknown as Scene;
    sceneMap[ss.id] = playable;
    order.push(String(ss.id));
    scenes.push(playable);
  }
  // As story publish: the first scene's playback settings stand for the story
  const firstSettings = order.length > 0 ? (scenesByStorySceneId[Number(order[0])] as { playbackSettings?: PlaybackSettings } | undefined)?.playbackSettings : undefined;
  return {
    formatVersion: MANIFEST_FORMAT_VERSION,
    runtimeVersion: RUNTIME_VERSION,
    version: 0,
    timestamp: Date.now(),
    story: { id: story.id, name: story.name },
    scenes,
    sceneOrder: order,
    graph: story.graph,
    sceneMap,
    playbackSettings: (playbackSettings ?? firstSettings ?? {}) as PlaybackSettings,
    media: {},
  } as StoryPackageManifest;
}
